import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useState } from "react";
import { clientAPI } from "../api/client";
import { formatNumber } from "../utils/utils";

const NUMBER_NFT_PER_PAGE = 25;
const queryKey = "referralEarning";

// backend nhận "MM-YYYY"
const toMonthParam = (d) => {
  if (!d) return undefined;
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${mm}-${d.getFullYear()}`;
};

// Tham số bộ lọc (kỳ xem, nguồn, người được giới thiệu) gửi lên backend, dùng
// chung cho danh sách và chi tiết
const toFilterOptions = (
  scope,
  monthDate,
  year,
  referralSource,
  referredId,
) => {
  const options = { scope };
  if (scope === "month" && monthDate) options.month = toMonthParam(monthDate);
  if (scope === "year" && year) options.year = year;
  if (referralSource) options.referralSource = referralSource;
  if (referredId) options.referredId = referredId;
  return options;
};

async function fetchReferralEarning(
  currentPage,
  search,
  scope,
  monthDate,
  year,
  referralSource,
  referredId,
) {
  try {
    const options = {
      limit: NUMBER_NFT_PER_PAGE,
      page: currentPage,
      ...toFilterOptions(scope, monthDate, year, referralSource, referredId),
    };

    if (search) options.search = search;

    const queryParams = new URLSearchParams(options).toString();
    const data = await clientAPI(
      "get",
      `/referralEarning/admin/list?${queryParams}`,
    );
    const totalPages = Math.ceil(data?.pagination?.total / NUMBER_NFT_PER_PAGE);
    const entries = data?.pagination?.total;
    const startEntry =
      (data?.pagination?.page - 1) * data?.pagination?.limit + 1;
    const endEntry = Math.min(
      data?.pagination?.page * data?.pagination?.limit,
      entries,
    );

    const newData = data?.items.map((item) => {
      return {
        id: item?.employeeId || "",
        code: item?.code || "-",
        name: item?.fullName || "-",
        referredCount: item?.referredCount || 0,
        approvedCount: item?.approvedCount || 0,
        noSourceCount: item?.noSourceCount || 0,
        companySourceCount: item?.companySourceCount || 0,
        personalSourceCount: item?.personalSourceCount || 0,
        provisionalAmount: `${formatNumber(item?.provisionalAmount || 0)} VNĐ`,
        paidAmount: `${formatNumber(item?.paidAmount || 0)} VNĐ`,
      };
    });

    return {
      data: newData,
      summary: data?.summary,
      referredOptions: data?.referredOptions || [],
      totalPages,
      entries,
      startEntry,
      endEntry,
    };
  } catch (error) {
    console.error("Error fetching referralEarning data:", error);
    return { data: [], totalPages: 0 };
  }
}

export function useReferralEarning(
  search,
  scope,
  monthDate,
  year,
  referralSource,
  referredId,
) {
  const [currentPage, setCurrentPage] = useState(1);

  const { data, refetch, isLoading, isRefetching } = useQuery({
    queryKey: [
      queryKey,
      currentPage,
      search,
      scope,
      monthDate,
      year,
      referralSource,
      referredId,
    ],
    queryFn: () =>
      fetchReferralEarning(
        currentPage,
        search,
        scope,
        monthDate,
        year,
        referralSource,
        referredId,
      ),
    refetchOnWindowFocus: false,
    staleTime: 30000,
    // Giữ dữ liệu cũ khi đổi bộ lọc để ô chọn người được giới thiệu không bị trống
    placeholderData: keepPreviousData,
    onError: (error) => {
      console.error("Error fetching referralEarning data:", error);
    },
  });

  return {
    referralEarning: data?.data,
    summary: data?.summary,
    referredOptions: data?.referredOptions || [],
    totalPages: data?.totalPages,
    refetch,
    isLoading,
    isRefetching,
    setCurrentPage,
    currentPage,
    entries: data?.entries,
    startEntry: data?.startEntry,
    endEntry: data?.endEntry,
  };
}

// Chi tiết: những người mà nhân viên `code` đã giới thiệu, kèm từng mốc thưởng
async function fetchReferralEarningDetail(
  code,
  scope,
  monthDate,
  year,
  referralSource,
  referredId,
) {
  try {
    const options = {
      code,
      ...toFilterOptions(scope, monthDate, year, referralSource, referredId),
    };
    const queryParams = new URLSearchParams(options).toString();
    const data = await clientAPI(
      "get",
      `/referralEarning/admin/referrals?${queryParams}`,
    );
    return data?.items || [];
  } catch (error) {
    console.error("Error fetching referralEarning detail:", error);
    return [];
  }
}

export function useReferralEarningDetail(
  code,
  scope,
  monthDate,
  year,
  referralSource,
  referredId,
) {
  const { data, isLoading, isRefetching } = useQuery({
    queryKey: [
      "referralEarningDetail",
      code,
      scope,
      monthDate,
      year,
      referralSource,
      referredId,
    ],
    queryFn: () =>
      fetchReferralEarningDetail(
        code,
        scope,
        monthDate,
        year,
        referralSource,
        referredId,
      ),
    enabled: !!code,
    refetchOnWindowFocus: false,
  });

  return {
    referrals: data || [],
    isLoading,
    isRefetching,
  };
}
